import { useState, useEffect, useCallback, useMemo, useRef, createContext } from "react"

// import exports from '../../src/aws-exports'
import { useAuthenticator } from '@aws-amplify/ui-react'

import {
  whoami as whoamiQuery,
} from '../../src/graphql/queries'
import { generateClient } from '@aws-amplify/api'

const WhoAmIContext = createContext()
export default WhoAmIContext

const BULK_UPLOAD_ENTITLEMENT = "bulkUpload"
const BEV_QUERY_TOOL_ENTITLEMENT = "bevQueryTool"

export const getWhoAmIState = (resolvedWhoAmI) => {
  const {
    firstName,
    lastName,
    role,
    customerId,
    customer: {
      customerName,
      entitlements = [],
      customerWebhookId,
      webhookManagementEnabled,
    } = {},
  } = resolvedWhoAmI

  return {
    businessName: customerName,
    customerId,
    name: { firstName, lastName },
    role,
    entitlements,
    webhookId: customerWebhookId,
    webhookManagementEnabled: webhookManagementEnabled === true,
    bulkUploadEnabled: entitlements.includes(BULK_UPLOAD_ENTITLEMENT),
    bevQueryToolEnabled: entitlements.includes(BEV_QUERY_TOOL_ENTITLEMENT),
  }
}

const ContextState = () => {
  const { user } = useAuthenticator((context) => [context.user])
  const userKey = user?.userId ?? user?.username ?? null
  const identity = useMemo(() => ({ userKey }), [userKey])

  // Migrating from Amplify V5 to V6.
  // https://docs.amplify.aws/react/migrate-v5-to-v6/migrate-graphql-api/
  const client = useMemo(() => generateClient(), []);
  
  const [loaded, setLoaded] = useState(false)
  const [businessName, setBusinessName] = useState('')
  const [name, setName] = useState({ firstName: '', lastName: '' })
  const [role, setRole] = useState('MEMBER')
  const [customerId, setCustomerId] = useState('')
  const [entitlements, setEntitlements] = useState([])
  const [webhookId, setWebhookId] = useState(null)
  const [webhookManagementEnabled, setWebhookManagementEnabled] = useState(false)
  const [bulkUploadEnabled, setBulkUploadEnabled] = useState(false)
  const [bevQueryToolEnabled, setBevQueryToolEnabled] = useState(false)
  const [error, setError] = useState(null)
  const [stateUserKey, setStateUserKey] = useState(null)
  const currentIdentity = useRef(identity)
  currentIdentity.current = identity
  const inFlightRefresh = useRef(null)

  const resetState = useCallback((nextError = null, nextLoaded = true) => {
    setError(nextError)
    setName({ firstName: '', lastName: '' })
    setRole('MEMBER')
    setCustomerId('')
    setEntitlements([])
    setBusinessName('')
    setWebhookId(null)
    setWebhookManagementEnabled(false)
    setBulkUploadEnabled(false)
    setBevQueryToolEnabled(false)
    setLoaded(nextLoaded)
  }, [])
  
  const refreshWhoAmI = useCallback(() => {
    if (!identity.userKey) return Promise.resolve()
    if (inFlightRefresh.current?.identity === identity) {
      return inFlightRefresh.current.promise
    }

    const request = { identity, promise: null }
    request.promise = client.graphql({
          query: whoamiQuery,
          authMode: 'userPool',
        }).then((result) => {
        if (currentIdentity.current !== request.identity) return
        const resolvedWhoAmI = result?.data?.whoami
        if (!resolvedWhoAmI) {
          resetState(new Error('whoami missing from response.'))
          setStateUserKey(request.identity.userKey)
          return
        }

        const nextState = getWhoAmIState(resolvedWhoAmI)

        setError(null)
        setName(nextState.name)
        setRole(nextState.role)
        setEntitlements(nextState.entitlements)
        setBusinessName(nextState.businessName)
        setCustomerId(nextState.customerId)
        setWebhookId(nextState.webhookId)
        setWebhookManagementEnabled(nextState.webhookManagementEnabled)
        setBulkUploadEnabled(nextState.bulkUploadEnabled)
        setBevQueryToolEnabled(nextState.bevQueryToolEnabled)
        setStateUserKey(request.identity.userKey)
        setLoaded(true)
      }).catch((err) => {
        if (currentIdentity.current !== request.identity) return
        console.error('Error loading whoami:', err)
        resetState(err)
        setStateUserKey(request.identity.userKey)
      }).finally(() => {
        if (inFlightRefresh.current === request) inFlightRefresh.current = null
      })
    inFlightRefresh.current = request
    return request.promise
  }, [identity, client, resetState])

  useEffect(() => {
    setStateUserKey(null)
    if (!identity.userKey) {
      resetState()
      return
    }
    resetState(null, false)
    refreshWhoAmI()
  }, [identity, refreshWhoAmI, resetState])

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshWhoAmI()
    }
    window.addEventListener('focus', refreshWhoAmI)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshWhoAmI)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [refreshWhoAmI])

  const stateBelongsToCurrentUser = stateUserKey === userKey

  return {
    businessName: stateBelongsToCurrentUser ? businessName : '',
    customerId: stateBelongsToCurrentUser ? customerId : '',
    loaded: stateBelongsToCurrentUser ? loaded : false,
    name: stateBelongsToCurrentUser ? name : { firstName: '', lastName: '' },
    role: stateBelongsToCurrentUser ? role : 'MEMBER',
    entitlements: stateBelongsToCurrentUser ? entitlements : [],
    webhookId: stateBelongsToCurrentUser ? webhookId : null,
    webhookManagementEnabled: stateBelongsToCurrentUser ? webhookManagementEnabled : false,
    refreshWhoAmI,
    bulkUploadEnabled: stateBelongsToCurrentUser ? bulkUploadEnabled : false,
    bevQueryToolEnabled: stateBelongsToCurrentUser ? bevQueryToolEnabled : false,
    error: stateBelongsToCurrentUser ? error : null,
  }
}

const WhoAmIContextProvider = (props) => {
  const state = ContextState()

  return (
    <WhoAmIContext.Provider value={state}>
      {props.children}
    </WhoAmIContext.Provider>
  ) 
}

export { WhoAmIContext, WhoAmIContextProvider }
